/*
    View to show SHA and deployment date for the given deployment

    Usage:
        var ProductDeploymentItem = require('@/views/product_deployment_item');
        var product_deployment_item = new ProductDeploymentItem({
            model: {{deployment}},
            can_view_code: true
        });

    Arguments: A hash including:
        model: A v5 deployment model object (models/v5/deployment.js)
        can_view_code: A Boolean that determines whether the SHA is clickable into a code editor view of this deployment

*/


let _old_deployment_item_template = `
{{#if show_polite_status}}
<td class="deployment-type actions">
<table><tr>
    <td class="current">
        <b>Current</b>
    </td>
    <td class="minimum">
        <button
            data-container="body"
            data-toggle="tooltip"
            data-placement="auto bottom"
            title="Click to set Minimum Deployment Devices running code older than this deployment will restart immediately and devices running code at this deployment or newer will restart conditionally"
            class="btn btn-link action set-minimum">Set Minimum</button>
        <b class="text-minimum">Minimum</b>
    </td>
</tr></table>
</td>
{{/if}}
<td>
  <div data-container="body" 
  data-toggle="tooltip" 
  data-placement="auto bottom">
  {{#date_UTC}}{{created_at}}{{/date_UTC}}
  </div>
  <div class="comment">{{description}}</div>
</td>
<td>
  {{#if can_view_code}}
  <a class="sha" href="{{deployment_route}}">{{short_sha sha}}</a>
  {{else}}
  {{short_sha sha}}
  {{/if}}
    <span class="download-code glyphicon glyphicon-download-alt 
      {{#unless can_download_code}}
      disabled
      {{/unless}}
      "
      {{#unless can_download_code}}
        title="You either do not have permissions to view/download code, or the code is unavailable. Please contact account owner or an Admin for more information."
      {{/unless}}
    ></span>
</td>
{{!--<td class="dg-name">{{deployed_to}}</td>--}}
{{!--<td class="dg-name">{{devicegroup_type}}</td>--}}
{{#if show_promoted }}<td class='promoted'>{{promoted}}</td>{{/if}}
<td class="dg-owner"></td>
{{#if show_delete_edit}}
<td class="actions">
  {{#unless is_pre_dg }}
    <button class="action redeploy btn btn-link">Redeploy</button>
  {{/unless}}
  <button class="action edit btn btn-link">Settings</button>
  {{#if show_delete}}
  <button class="action delete btn btn-link">Delete</button>
  {{/if}}
</td>
{{/if}}
`;
let deployment_item_template = `
{{#if sha}}
    {{#if can_view_code}}
        <strong><i class="fa fa-code" data-id="devicegroups"></i> Deployment</strong> <a class="sha" href="{{deployment_route}}">{{short_sha sha}}</a>
    {{else}}
        <strong><i class="fa fa-code" data-id="devicegroups"></i> Deployment</strong> {{short_sha sha}}
    {{/if}}
        | <span class="download-code glyphicon glyphicon-download-alt 
            {{#unless can_download_code}}disabled{{/unless}}"
            {{#unless can_download_code}} 
                title="You either do not have permissions to view/download code, or the code is unavailable. Please contact account owner or an Admin for more information."
            {{/unless}}
        ></span>
{{else}}
   <strong><i class="fa fa-code" data-id="devicegroups"></i> Deployment</strong> --
{{/if}}`;

import _ from 'underscore';
import Radio from 'backbone.radio';
import Marionette from 'backbone.marionette';
import Handlebars from 'handlebars';
import moment from 'moment';
import short_sha from '@/lib/short_sha';
import ConfirmationDialog from '@/views/dialogs/confirmation';

var globalCh = Radio.channel('global');
/**
 * Only show shas for devicegroups in the following types
 */
function has_a_viewable_sha(deployment) {
    var devicegroup = deployment.rel.get('devicegroup');
    return devicegroup && (devicegroup.type !== devicegroup.TYPES_MAP.production) && (devicegroup.type !== devicegroup.TYPES_MAP.factoryfixture);
}
function set_dg_listeners(rel, devicegroup) {
    var prev_devicegroup = rel.previous('devicegroup');
    if (prev_devicegroup) {
        this.stopListening(prev_devicegroup);
    }

    /*
        *  on_change_devicegroup
        *
        *  Checks to see if a view is destroyed before attempting to render.
        *  Views connected through models will sometimes attempt to destroy
        *  something that has already been destroyed. Ex: the relationship
        *  between fixture device groups & factory settings dialog.
        */
    this.listenTo(devicegroup, 'change', this.on_change_devicegroup);
    this.listenTo(devicegroup.rel.get('creator'), 'change', this.on_change_devicegroup);
}
var DeploymentItemView = Marionette.View.extend({
    template: Handlebars.compile(deployment_item_template),
    tagName: 'tr',
    ui: {
        sha: '.sha',
        creator: '.dg-owner',
        delete: '.actions .delete',
        edit: '.actions .edit',
        redeploy: '.actions .redeploy',
        set_minimum: 'button.set-minimum',
        minimum_indicator: '.minimum b',
        current_indicator: '.current b',
        download_code: '.download-code',
        tooltips: '[data-toggle="tooltip"]'
    },
    regions: {
        creator: '@ui.creator'
    },
    events: {
        'click @ui.sha': 'show_code',
        'click @ui.delete': 'show_delete_dialog',
        'click @ui.edit': 'show_edit_dialog',
        'click @ui.redeploy': 'show_redeploy_dialog',
        'click @ui.set_minimum': 'show_set_minimum_confirmation_dialog',
        'click @ui.download_code': 'download_code',
    },
    modelEvents: {
        'change': 'render'
    },
    initialize: function() {
        var devicegroup = this.model && this.model.rel.get('devicegroup') || undefined;
        if ( this.isDestroyed() == true || !devicegroup ) return;
        this.model.rel.load_all();
        // Attaching a listener to listen to the devicegroup model, so
        // we know when to display a devicegroup's name
        this.listenTo(this.model.rel, 'change:devicegroup', set_dg_listeners);
        if (devicegroup) {
            set_dg_listeners.call(this, this.model.rel, devicegroup);
        }
        this.listenTo(
            devicegroup.rel,
            'change:current_deployment',
            this.set_current_deployment_indicator
        );
        this.listenTo(
            devicegroup.rel,
            'change:min_supported_deployment',
            this.set_minimum_deployment_indicator
        );
        this.listenTo(
            devicegroup,
            'change:name',
            this.render
        );
    },
    set_current_deployment_indicator: function() {
        if ( !this.isRendered() ) return;
        
        var devicegroup = this.model.rel.get('devicegroup');
        if ( !devicegroup ) return;

        var current_deployment = devicegroup.rel.get('current_deployment');
        if ( !current_deployment ) return;
        if ( this.model.id == current_deployment.id ) {
            this.ui.current_indicator.css('visibility', 'visible');
        } else {
            this.ui.current_indicator.css('visibility', 'hidden');
        }
    },
    set_minimum_deployment_indicator: function() {
        if ( !this.isRendered ) return;

        var devicegroup = this.model.rel.get('devicegroup');
        if ( !devicegroup ) return;

        var minimum_deployment = devicegroup.rel.get('min_supported_deployment');
        if ( !minimum_deployment ) return;
        var newer_than_min = 
                moment(this.model.get('created_at')) 
            > moment(minimum_deployment.get('created_at'));

        // Set the indiactor and visibility of the button
        if (this.model.id == minimum_deployment.id) {
            this.ui.minimum_indicator.show();
            this.ui.set_minimum.hide();
        } else {
            this.ui.minimum_indicator.hide();
            if (newer_than_min) { 
                this.ui.set_minimum.show();
            } else {
                this.ui.set_minimum.hide();
            }
        }
    },
    show_creator: function() {
        var creator = this.model.rel.get('creator');
        try {
        this.showChildView('creator', new Marionette.View({
            modelEvents: { 'change': 'render' },
            model: creator,
            template: Handlebars.compile('{{username}}'),
        }));
        } catch (e) {
            // Ignore missing region errors
            if (e.message.indexOf('must exist in DOM') === -1) {
                throw e;
            }
        }
    },
    onRender: function() {
        var tooltip_options = { delay: { show: 500, hide: 100 }};
        // var creator = this.model.rel.get('creator');
        // if (creator) {
        //    this.show_creator();
        // } else {
        //    this.listenToOnce(this.model.rel, 'change:creator', this.show_creator);
        // }
        // Give each item a data-id and data-type
        // This pattern has the potential for use in all v5 ItemViews
        if (this.model) {
            this.el.dataset.id = this.model.id;
            this.el.dataset.type = this.model.type;
            // Set visibility of current, min markers and buttons
            this.set_current_deployment_indicator();
            this.set_minimum_deployment_indicator();
            this.ui.tooltips.tooltip(tooltip_options);
        } else {
            this.el.dataset.id = 'undefined';
            this.el.dataset.type = 'undefined';
        }
    },
    /*
        *  Check to see if a view is destroyed before attempting to render.
        */
    on_change_devicegroup: function(){
        if ( this.isDestroyed() ) return;
    },
    templateContext: function() {
        var active = globalCh.request('active_account');
        var primary = globalCh.request('primary_account');
        // TODO: refactor this view to use region for devicegroup info
        var devicegroup = this.model && this.model.rel.get('devicegroup') || undefined;
        if ( !devicegroup ) return;
        var current_deployment = devicegroup.rel.get('current_deployment');
        var minimum_deployment = devicegroup.rel.get('min_supported_deployment');
        var devicegroup_name = devicegroup.get('name');
        var devicegroup_type = devicegroup.type.replace('_devicegroup', '');
        var is_pre_dg = false;
        var promoted = this.model.get('flagged');
        var show_deployed_to = _.has(this.options, 'show_deployed_to') ? this.getOption('show_deployed_to') : true;
        var deployment_route;
        var show_promoted = _.has(this.options, 'show_promoted') ? this.getOption('show_promoted') : true;
        var show_delete_edit = true;
        var show_delete = active.permits(primary, 
                                        'delete_' 
                                        + devicegroup_type 
                                        + '_deployment'
        );
        if ( devicegroup.type === 'pre_dut_devicegroup' ) {
            is_pre_dg = true;
            show_delete = active.permits(primary, 'delete_pre_factoryfixture_deployment');
        } else if ( devicegroup.type ===  'dut_devicegroup' ) {
            is_pre_dg = true;
            show_delete = active.permits(primary, 'delete_factoryfixture_deployment');
        }
        let can_download_code = this.model.can_download_code(devicegroup);

        var is_current_deployment = (current_deployment && current_deployment.id == this.model.id);
        var is_minimum_deployment = (minimum_deployment && minimum_deployment.id == this.model.id);
        var show_polite_status = this.getOption('show_polite_status');
        return {
            is_current_deployment: is_current_deployment,
            is_minimum_deployment: is_minimum_deployment,
            show_deployed_to: show_deployed_to,
            show_promoted: show_promoted,
            show_polite_status: show_polite_status,
            deployed_to: devicegroup_name,
            devicegroup_type: devicegroup_type,
            can_view_code: this.getOption('can_view_code')
                && has_a_viewable_sha(this.model),
            deployment_route: deployment_route,
            is_pre_dg: is_pre_dg,
            promoted: promoted,
            show_delete_edit: show_delete_edit,
            show_delete: show_delete,
            can_download_code,
        };
    },
    show_code: function(e) {
        // Allow users to control-click
        if (e.ctrlKey || e.metaKey) { return; }
        e.preventDefault();

        // Show device group pane if type is development or factory
        var devicegroup = this.model.rel.get('devicegroup');

        globalCh.request('showCodeEditor', this.model, devicegroup);
    },
    show_edit_dialog: function() {
        globalCh.request('show_edit_deployment_dialog', this.model);
    },
    show_delete_dialog: function () {
        var options = {
            success: function(deployment) {
                var alertsCh = Radio.channel('alerts');
                var message = 'Successfully deleted '
                            + deployment.get('sha');
                alertsCh.request('showAlert', {
                    messageType: 'success',
                    message: message
                });
            }
        };
        globalCh.request('show_delete_deployment_dialog', this.model, options);
        },
    show_set_minimum_confirmation_dialog: function(e) {
        var dialog = new ConfirmationDialog({
            callback: this.set_minimum.bind(this),
            message: {
                title: 'Setting ' + short_sha(this.model.get('sha')) + ' to minimum',
                body: 'Devices running code older than this new minimum will restart immediately and devices running code at this new minimum or newer will restart conditionally. '
                    + 'Once minimum is set, you won\'t be able to set the minimum to anything earlier. <br /><br />'
                    + 'Are you sure you want to set this deployment to be the new minimum?',
                primary_action: 'Set Minimum Deployment'
            }
        });
        globalCh.request('showDialog', dialog);
    },
    set_minimum: function() {
        var devicegroup = this.model.rel.get('devicegroup');
        var deployment = this.model;
        var success = 'Deployment with SHA ' 
                    + short_sha(this.model.get('sha'))
                    + ' is now the minimum supported deployment for '
                    + 'the device group "'
                    + devicegroup.get('name')
                    + '."'
        ;
        function show_success() {
            globalCh.request('layout_alert', success, 'success', 10000);
        }
        function set_minimum() {
            return devicegroup.set_minimum_deployment(deployment);
        }
        function conditional_restart() {
            devicegroup.conditional_restart();
        }
        function error(reason) {
            console.error(reason);
        }
        return devicegroup.load()
            .done(set_minimum)
            .done(conditional_restart)
            .done(show_success)
            .fail(error)
        ;
    },
    show_redeploy_dialog: function() {
        // pass show_create_deployment_dialog a devicegroup & deployment, will cause the selection menu not
        // to be rendered
        globalCh.request('show_create_deployment_dialog', this.model.rel.get('devicegroup'), this.model);
    },
    download_code: function(){
        if (this.model.can_download_code(this.model.rel.get('devicegroup'))) {
            this.model.download();
        } else {
            return;
        }
    },
});

export default DeploymentItemView;

